import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { Mail, Lock, User } from 'lucide-react';
import { isDevAuthEnabled, devSignIn } from '../utils/devAuth';
// NinjaLayer now rendered globally in App.js

const Register = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', { name, email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Account created successfully!');
      navigate('/projects');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Registration failed';
      if (isDevAuthEnabled()) {
        const { user } = devSignIn({ email, name });
        toast.success(`Dev account created: ${user.name}`);
        navigate('/projects');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const response = await api.get('/auth/google');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data.message || 'Google OAuth not configured.');
      }
    } catch (err) {
      if (isDevAuthEnabled()) {
        const { user } = devSignIn({ email: 'dev-google@example.com', name: 'Dev Google' });
        toast.success(`Dev Google sign-up: ${user.name}`);
        navigate('/projects');
      } else {
        toast.error('Failed to initiate Google OAuth. Network Error');
      }
    }
  };

  return (
    <>
      {/* AuthShell provides NinjaLayer masthead + centered glass card */}

      <Card className="border border-emerald-500/30 bg-black/70 backdrop-blur-2xl shadow-2xl shadow-emerald-500/20">
        <CardContent className="pt-6">
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-emerald-300">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-emerald-500" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-black/50 border-emerald-500/30 text-white placeholder:text-slate-500 focus:border-emerald-400 focus:ring-emerald-400/20"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-emerald-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-emerald-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-black/50 border-emerald-500/30 text-white placeholder:text-slate-500 focus:border-emerald-400 focus:ring-emerald-400/20"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-emerald-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-emerald-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-black/50 border-emerald-500/30 text-white placeholder:text-slate-500 focus:border-emerald-400 focus:ring-emerald-400/20"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              <p className="text-xs text-emerald-400/70">Must be at least 6 characters</p>
            </div>

            <Button type="submit" className="w-full bg-gradient-to-r from-emerald-600 to-orange-500 hover:from-emerald-500 hover:to-orange-400 text-white font-semibold shadow-lg shadow-emerald-500/25" disabled={loading} data-testid="register-button">
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-transparent px-2 text-slate-400">Or continue with</span>
            </div>
          </div>

          <Button type="button" variant="outline" className="w-full border-slate-600 text-white hover:bg-white/10" onClick={handleGoogleSignup} data-testid="google-signup-button">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Sign up with Google
          </Button>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-white hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </>
  );
};

export default Register;